// Module 1 — /workbench/:scenarioId
//
// Three-pane ResizablePanelGroup:
//
//    ┌──── left rail ────┬────────── centre editor ──────────┬── evidence ──┐
//    │ scenario tree     │ section editor (ParamField grid)  │ validation   │
//    │                   │                                   │ + provenance │
//    └───────────────────┴───────────────────────────────────┴──────────────┘
//
// The page owns:
//   • scenario doc (GET on mount, PATCH on edit with debounce)
//   • validation result (POST /validation on debounce after edit)
//   • acknowledgement POSTs
//   • the header "Run" gate
//
// The edit cycle is intentionally conservative: every ParamField edit
// builds a local copy of scenario.parameters and fires a PATCH; when
// the PATCH resolves we replace the scenario state with the fresh
// server copy. Validation and acknowledgements operate off the fresh
// scenario — we never optimistically mutate acknowledgements.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ExternalLink, PlayCircle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from "@/components/ui/resizable";
import { formatApiError } from "@/lib/formatApiError";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import SpinnerButton from "@/components/SpinnerButton";
import WorkbenchTree from "@/components/workbench/WorkbenchTree";
import WorkbenchEditor from "@/components/workbench/WorkbenchEditor";
import WorkbenchEvidence from "@/components/workbench/WorkbenchEvidence";
import { SECTIONS } from "@/components/workbench/sections";
import WorkbenchOptimisationPanel from "@/components/optimisation/WorkbenchOptimisationPanel";
import { runs as runsApi, sim } from "@/api/endpoints";

const PATCH_DEBOUNCE_MS = 400;
const VALIDATE_DEBOUNCE_MS = 500;

function parseSectionFromHash(hash) {
    if (!hash) return null;
    const m = /section=([a-zA-Z_]+)/.exec(hash);
    if (!m) return null;
    const want = m[1];
    return SECTIONS.find((s) => s.id === want)?.id || null;
}

// ---------------------------------------------------------------------
// Header with the big "Run" button + validation-gate tooltip.
// ---------------------------------------------------------------------
function WorkbenchHeader({ scenario, validation, onRun, isRunning }) {
    const blocking = validation?.issues?.filter((i) => i.severity === "blocking") || [];
    const runnable = !!validation?.runnable;
    const tooltip = blocking.length
        ? `Fix ${blocking.length} blocking issue${blocking.length === 1 ? "" : "s"} before running:\n- ${blocking
              .map((i) => i.message)
              .slice(0, 5)
              .join("\n- ")}`
        : "Submit this scenario to a new Monte Carlo run (seed 42, kinematic).";

    return (
        <div
            className="border-b border-ux-border-1 bg-ux-bg-1 px-4 py-3 flex items-center gap-3"
            data-testid="workbench-header"
        >
            <Link
                to="/plan/scenarios"
                className="inline-flex items-center gap-1 text-[12px] text-ux-text-3 hover:text-ux-text-1"
                data-testid="workbench-header-back"
            >
                <ChevronLeft className="w-3.5 h-3.5" /> Scenarios
            </Link>
            <div className="flex-1 min-w-0">
                <h1 className="text-[14px] text-ux-text-1 font-medium truncate" data-testid="workbench-header-name">
                    {scenario?.name || "Scenario"}
                </h1>
                <p className="text-[11px] text-ux-text-3 font-mono truncate">
                    id {scenario?.id || "…"} · owner {scenario?.owner_username || "—"}
                </p>
            </div>
            <span
                className={[
                    "inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border",
                    runnable
                        ? "border-ux-success/40 text-ux-success bg-ux-success/10"
                        : "border-ux-warning/40 text-ux-warning bg-ux-warning/10",
                ].join(" ")}
                data-testid="workbench-header-runnable"
            >
                <ShieldCheck className="w-3 h-3" />
                {runnable ? "Runnable" : `${blocking.length} blocking`}
            </span>
            <TooltipProvider delayDuration={80}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            <SpinnerButton
                                disabled={!runnable || !!isRunning}
                                loading={!!isRunning}
                                onClick={() => onRun && onRun()}
                                testId="workbench-run-button"
                            >
                                <PlayCircle className="w-3.5 h-3.5" />
                                Run
                            </SpinnerButton>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent
                        side="bottom"
                        className="max-w-[320px] whitespace-pre-line bg-ux-bg-1 text-ux-text-1 border-ux-border-1 text-[11px] font-mono"
                    >
                        {tooltip}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>
    );
}

// ---------------------------------------------------------------------
// Main page.
// ---------------------------------------------------------------------
export default function WorkbenchPage() {
    const { scenarioId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [scenario, setScenario] = useState(null);
    const [loadErr, setLoadErr] = useState(null);
    const [loading, setLoading] = useState(true);
    const [rules, setRules] = useState([]);
    const [validation, setValidation] = useState(null);
    const [validating, setValidating] = useState(false);
    const [activeSection, setActiveSection] = useState(
        () => parseSectionFromHash(location.hash) || "airframe",
    );

    const patchTimer = useRef(null);
    const validateTimer = useRef(null);

    // ── Initial fetch ──
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const [s, r] = await Promise.all([
                    sim.scenarios.get(scenarioId),
                    sim.validationRules().catch(() => []),
                ]);
                if (cancelled) return;
                setScenario(s);
                setRules(r);
                // Immediately request a validation snapshot.
                const v = await sim.scenarios.validate(scenarioId);
                if (cancelled) return;
                setValidation(v);
            } catch (e) {
                if (!cancelled) {
                    setLoadErr(
                        formatApiError(e, "Failed to load scenario"),
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [scenarioId]);

    // Keep #section=… in the URL in sync with state so Cmd+K deep links work.
    useEffect(() => {
        const want = `#section=${activeSection}`;
        if (location.hash !== want) {
            navigate(
                { pathname: location.pathname, search: location.search, hash: want },
                { replace: true },
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const scheduleValidate = useCallback(() => {
        if (validateTimer.current) clearTimeout(validateTimer.current);
        validateTimer.current = setTimeout(async () => {
            setValidating(true);
            try {
                const v = await sim.scenarios.validate(scenarioId);
                setValidation(v);
            } catch {
                // Silent — we'll retry on the next edit.
            } finally {
                setValidating(false);
            }
        }, VALIDATE_DEBOUNCE_MS);
    }, [scenarioId]);

    // ── Parameter edit → PATCH → re-validate ──
    const handleParameterChange = useCallback(
        ({ section, name, value, unit }) => {
            setScenario((prev) => {
                if (!prev) return prev;
                const nextParams = { ...(prev.parameters || {}) };
                const bucket = { ...(nextParams[section] || {}) };
                const existing = bucket[name] || { source: "user", confidence: "provisional" };
                bucket[name] = {
                    ...existing,
                    value,
                    unit,
                    source: existing.source || "user",
                    confidence: existing.confidence || "provisional",
                };
                nextParams[section] = bucket;
                return { ...prev, parameters: nextParams };
            });

            if (patchTimer.current) clearTimeout(patchTimer.current);
            patchTimer.current = setTimeout(async () => {
                try {
                    setScenario((prev) => {
                        if (!prev) return prev;
                        // Fire the PATCH with the current known parameters.
                        sim.scenarios
                            .patch(scenarioId, { parameters: prev.parameters })
                            .then((fresh) => {
                                setScenario((s2) => (s2 ? { ...s2, ...fresh } : fresh));
                                scheduleValidate();
                            })
                            .catch((e) => {
                                toast.error(
                                    formatApiError(e, "Failed to save parameter"),
                                );
                            });
                        return prev;
                    });
                } catch (e) {
                    toast.error(e?.message || "Failed to save parameter");
                }
            }, PATCH_DEBOUNCE_MS);
        },
        [scenarioId, scheduleValidate],
    );

    // Refetch the scenario + re-validate. Fired by every pane after it
    // successfully attaches / detaches something. Fixes the "attach
    // doesn't persist" bug where the POST succeeded server-side but the
    // React tree, evidence rail, and pane body never saw the new state.
    const reloadScenario = useCallback(async () => {
        try {
            const fresh = await sim.scenarios.get(scenarioId);
            setScenario(fresh);
            scheduleValidate();
        } catch (e) {
            toast.error(
                formatApiError(e, "Failed to refresh scenario"),
            );
        }
    }, [scenarioId, scheduleValidate]);

    const handleAcknowledge = useCallback(
        async (rule_id) => {
            try {
                const fresh = await sim.scenarios.acknowledge(scenarioId, {
                    rule_id,
                });
                setScenario(fresh);
                scheduleValidate();
                toast.success(`Acknowledged "${rule_id}"`);
            } catch (e) {
                toast.error(
                    formatApiError(e, "Failed to acknowledge rule"),
                );
            }
        },
        [scenarioId, scheduleValidate],
    );

    // State for the Run button + inline backend picker.
    const [isRunning, setIsRunning] = useState(false);

    const handleRun = useCallback(
        async (opts = {}) => {
            if (!validation?.runnable) return;
            setIsRunning(true);
            try {
                const body = {
                    scenario_id: scenarioId,
                    run_type: "monte_carlo",
                    ...(opts.seed !== undefined ? { seed: opts.seed } : { seed: 42 }),
                    ...(opts.seeds !== undefined ? { seeds: opts.seeds } : {}),
                    ...(opts.backend
                        ? { requested_dynamics_backend: opts.backend }
                        : {}),
                };
                const created = await runsApi.submit(body);
                navigate(`/analyse/runs/${created.id}`);
            } catch (e) {
                // A 400 from the backend may carry the validation gate
                // blocking-rule payload — splice it into the local
                // validation state so the panel surfaces it immediately.
                const detail = e?.response?.data?.detail;
                if (
                    e?.response?.status === 400 &&
                    detail &&
                    Array.isArray(detail.blocking)
                ) {
                    setValidation((prev) => ({
                        ...(prev || {}),
                        runnable: false,
                        blocking: detail.blocking,
                    }));
                    toast.error(
                        `Scenario blocked by ${detail.blocking.length} validation rule${detail.blocking.length === 1 ? "" : "s"}.`,
                    );
                } else {
                    toast.error(
                        (typeof detail === "string" && detail) ||
                            e?.message ||
                            "Failed to start the run.",
                    );
                }
            } finally {
                setIsRunning(false);
            }
        },
        [navigate, scenarioId, validation?.runnable],
    );

    // ---------------- render ----------------
    if (loading) {
        return (
            <div
                className="flex items-center justify-center h-[60vh] text-ux-text-3 font-mono text-[13px]"
                data-testid="workbench-loading"
            >
                Loading scenario…
            </div>
        );
    }

    if (loadErr) {
        return (
            <div className="p-6" data-testid="workbench-error">
                <div className="rounded-md border border-ux-danger/40 bg-ux-danger-soft/40 p-4 text-ux-danger text-[13px]">
                    <p className="font-medium">Unable to open scenario.</p>
                    <p className="text-[12px] mt-1">{loadErr}</p>
                    <Link
                        to="/plan/scenarios"
                        className="inline-flex items-center gap-1 mt-3 text-ux-accent"
                    >
                        Back to Scenarios <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className="flex flex-col h-[calc(100vh-60px-54px)] min-h-[520px] -mx-4 sm:-mx-6 lg:-mx-8 -my-6"
            data-testid="workbench-page"
        >
            <WorkbenchHeader
                scenario={scenario}
                validation={validation}
                onRun={handleRun}
                isRunning={isRunning}
            />
            <ResizablePanelGroup
                direction="horizontal"
                className="flex-1 min-h-0 border-t border-ux-border-1 bg-ux-bg-0"
            >
                <ResizablePanel
                    defaultSize={18}
                    minSize={14}
                    maxSize={32}
                    className="bg-ux-bg-1"
                >
                    <WorkbenchTree
                        scenario={scenario}
                        activeSection={activeSection}
                        onSelect={setActiveSection}
                    />
                </ResizablePanel>
                <ResizableHandle className="bg-ux-border-1 w-px" />
                <ResizablePanel defaultSize={54} minSize={40} className="bg-ux-bg-1">
                    {activeSection === "optimisation" ? (
                        <WorkbenchOptimisationPanel
                            scenarioId={scenarioId}
                            scenarioName={scenario?.name}
                        />
                    ) : (
                        <WorkbenchEditor
                            scenario={scenario}
                            activeSection={activeSection}
                            onParameterChange={handleParameterChange}
                            onScenarioChanged={reloadScenario}
                        />
                    )}
                </ResizablePanel>
                <ResizableHandle className="bg-ux-border-1 w-px" />
                <ResizablePanel
                    defaultSize={28}
                    minSize={20}
                    maxSize={38}
                    className="bg-ux-bg-1"
                >
                    <WorkbenchEvidence
                        scenario={scenario}
                        activeSection={activeSection}
                        validation={validation}
                        validating={validating}
                        rules={rules}
                        onAcknowledge={handleAcknowledge}
                    />
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
}

// The preferred landing is #section=airframe — but any SECTIONS.id also
// works, which is what the CommandPalette uses for the "jump to node"
// behaviour spec'd in the brief.
export { parseSectionFromHash };
